import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import useTeacherService from '../../api/services/teacherService';
import useDutiesWeeksService from '../../api/services/DutiesWeeksService';

const AssignTeacherDialog = ({ open, onClose, onSuccess, dutiesWeekId, dutiesWeekName }) => {
  const { fetchTeachers, teachers } = useTeacherService();
  const { assignTeacherToDutiesWeek, unassignTeacherFromDutiesWeek, fetchTeachersByDutiesWeek } = useDutiesWeeksService();
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unassigningTeacherId, setUnassigningTeacherId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchTeachers(0, 100, 'ACTIVE');
      if (dutiesWeekId) {
        loadAssignedTeachers();
      }
    }
  }, [open, dutiesWeekId]);

  const loadAssignedTeachers = async () => {
    setIsLoading(true);
    try {
      const teachers = await fetchTeachersByDutiesWeek(dutiesWeekId);
      setAssignedTeachers(teachers || []);
    } catch (error) {
      console.error('Error loading assigned teachers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedTeacherId) {
      return;
    }

    try {
      await assignTeacherToDutiesWeek(dutiesWeekId, selectedTeacherId, () => {
        setSelectedTeacherId('');
        setSelectedTeacher(null);
        loadAssignedTeachers();
        if (onSuccess) onSuccess();
        handleClose();
      });
    } catch (error) {
      console.error('Error assigning teacher:', error);
    }
  };

  const handleUnassign = async (teacherId) => {
    if (!teacherId || !dutiesWeekId) {
      return;
    }

    setUnassigningTeacherId(teacherId);
    try {
      await unassignTeacherFromDutiesWeek(dutiesWeekId, teacherId, () => {
        loadAssignedTeachers();
        if (onSuccess) onSuccess();
        handleClose();
      });
    } catch (error) {
      console.error('Error unassigning teacher:', error);
    } finally {
      setUnassigningTeacherId(null);
    }
  };

  const handleClose = () => {
    setSelectedTeacherId('');
    setSelectedTeacher(null);
    setAssignedTeachers([]);
    onClose();
  };

  const handleTeacherChange = (event, newValue) => {
    if (newValue) {
      setSelectedTeacher(newValue);
      setSelectedTeacherId(newValue.id);
    } else {
      setSelectedTeacher(null);
      setSelectedTeacherId('');
    }
  };

  const availableTeachers = teachers.filter(
    teacher => !assignedTeachers.some(assigned => assigned.id === teacher.id)
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Teacher to Duties Week</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Week: {dutiesWeekName}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              options={availableTeachers}
              getOptionLabel={(option) => option.name || ''}
              value={selectedTeacher}
              onChange={handleTeacherChange}
              disabled={availableTeachers.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Teacher"
                  variant="filled"
                  placeholder={availableTeachers.length === 0 ? "No available teachers" : "Search and select a teacher..."}
                />
              )}
              noOptionsText="No teachers found"
              filterOptions={(options, { inputValue }) => {
                return options.filter((option) =>
                  option.name?.toLowerCase().includes(inputValue?.toLowerCase() || '')
                );
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>

          {assignedTeachers.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Currently Assigned Teachers:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {assignedTeachers.map((teacher) => (
                  <Box
                    key={teacher.id}
                    sx={{
                      p: 1,
                      backgroundColor: 'action.hover',
                      borderRadius: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2">{teacher.name}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleUnassign(teacher.id)}
                      disabled={unassigningTeacherId === teacher.id}
                      sx={{
                        ml: 1,
                        '&:hover': {
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Close
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="secondary"
          disabled={!selectedTeacherId || availableTeachers.length === 0}
        >
          Assign Teacher
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignTeacherDialog;

